/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { SET_NEGOTIABLE_QUOTE_TEMPLATE_SHIPPING_ADDRESS_MUTATION } from './graphql/setNegotiableQuoteTemplateShippingAddress';
import { transformQuoteTemplate } from '@/quote-management/data/transforms';
import { state } from '@/quote-management/lib/state';
import { events } from '@adobe-commerce/event-bus';

export interface NegotiableQuoteAddressInput {
  city: string;
  company?: string;
  countryCode: string;
  fax?: string;
  firstname: string;
  lastname: string;
  middlename?: string;
  postcode?: string;
  prefix?: string;
  region?: string;
  regionId?: number;
  saveInAddressBook?: boolean;
  street: string[];
  suffix?: string;
  telephone?: string;
  vatId?: string;
}

export interface NegotiableQuoteTemplateShippingAddressInput {
  address?: NegotiableQuoteAddressInput;
  customerAddressUid?: string;
  customerNotes?: string;
}

export interface AddQuoteTemplateShippingAddressParams {
  templateId: string;
  shippingAddress: NegotiableQuoteTemplateShippingAddressInput;
}

export const addQuoteTemplateShippingAddress = async (
  params: AddQuoteTemplateShippingAddressParams
) => {
  if (!params.templateId) {
    throw new Error('Template ID is required');
  }

  if (!params.shippingAddress) {
    throw new Error('Shipping address is required');
  }

  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  // Validate that at least one of address or customerAddressUid is provided
  if (
    !params.shippingAddress.address &&
    !params.shippingAddress.customerAddressUid
  ) {
    throw new Error('Either address or customerAddressUid must be provided');
  }

  try {
    const results: any = await fetchGraphQl(
      SET_NEGOTIABLE_QUOTE_TEMPLATE_SHIPPING_ADDRESS_MUTATION,
      {
        variables: {
          templateId: params.templateId,
          shippingAddress: {
            address: params.shippingAddress.address
              ? {
                  city: params.shippingAddress.address.city,
                  company: params.shippingAddress.address.company,
                  country_code: params.shippingAddress.address.countryCode,
                  fax: params.shippingAddress.address.fax,
                  firstname: params.shippingAddress.address.firstname,
                  lastname: params.shippingAddress.address.lastname,
                  middlename: params.shippingAddress.address.middlename,
                  postcode: params.shippingAddress.address.postcode,
                  prefix: params.shippingAddress.address.prefix,
                  region: params.shippingAddress.address.region,
                  region_id: params.shippingAddress.address.regionId,
                  save_in_address_book:
                    params.shippingAddress.address.saveInAddressBook,
                  street: params.shippingAddress.address.street,
                  suffix: params.shippingAddress.address.suffix,
                  telephone: params.shippingAddress.address.telephone,
                  vat_id: params.shippingAddress.address.vatId,
                }
              : undefined,
            customer_address_uid: params.shippingAddress.customerAddressUid,
            customer_notes: params.shippingAddress.customerNotes,
          },
        },
      }
    );

    if (!results?.data?.setNegotiableQuoteTemplateShippingAddress) {
      throw new Error('No quote template data received');
    }

    const transformedData = transformQuoteTemplate(
      results.data.setNegotiableQuoteTemplateShippingAddress
    );

    if (!transformedData) {
      throw new Error('Failed to transform quote template data');
    }

    events.emit('quote-management/quote-template-data', {
      quoteTemplate: transformedData,
      permissions: state.permissions,
    });

    return transformedData;
  } catch (error) {
    return Promise.reject(error);
  }
};
